import React, { FC, useEffect, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { each, filter, flatten, includes, isEmpty, map, unionBy } from 'lodash';
import { useSelector } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';

import { Form } from 'components/DataEntry/Form';
import { getIcon } from 'components/Icons/Components';
import { selectApp } from 'containers/App/selectors';
import { AllowListCriteria } from 'containers/Inventory/AllowList/Components/AllowListWizard/Steps/AllowListCriteria';
import { AllowListStatus } from 'containers/Inventory/AllowList/Components/AllowListWizard/Steps/AllowListStatus';
import { getAccountLabel } from 'containers/Inventory/AllowList/Components/AllowListWizard/utils';
import { useAllowListContext } from 'containers/Inventory/AllowList/context';
import { selectAllowList } from 'containers/Inventory/AllowList/selector';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';

interface Props {}

export const AllowListSettings: FC<Props> = () => {
  const {
    handleSubmit,
    onCancel,
    actionType,
    updateFormData,
  } = useAllowListContext();
  const { regions, tagKeys } = useSelector(selectSharedState);
  const { accountMapping } = useCloudAccountId();
  const { environments } = useSelector(selectApp);
  const { formData, allowList, allowListAction, allowListStatus } = useSelector(
    selectAllowList,
  );
  const [environmentOptions, setEnvironmentOptions] = useState<
    Record<string, any>[]
  >([]);
  const [selectedAccounts, setSelectedAccounts] = useState<
    Record<string, any>[]
  >([]);
  const [accountOptions, setAccountOptions] = useState<Record<string, any>[]>(
    [],
  );
  const [selectedEnvs, setSelectedEnvs] = useState<Record<string, any>[]>([]);
  const [isDisabled, setIsDisabled] = useState(true);

  //set environment options
  useEffect(() => {
    let options: Record<string, any>[] = [];
    each(environments.data, o => {
      if (!isEmpty(o.account_ids))
        options.push({ label: o?.name, value: o?.id, data: o });
    });
    setEnvironmentOptions(options);
  }, [environments.data]);

  //set selected environments
  useEffect(() => {
    if (!isEmpty(formData?.ids)) {
      const envIds = map(formData.ids, o => o.environment_id);
      const envs = filter(environmentOptions, a => includes(envIds, a?.value));
      setSelectedEnvs(envs);
    }
  }, [environmentOptions, formData?.ids]);

  //set account options
  useDeepCompareEffect(() => {
    if (!!selectedEnvs.length) {
      let options = map(selectedEnvs, o => ({
        label: o?.data?.name,
        options: map(o?.data?.account_ids, a => {
          const account = accountMapping?.[a];
          // Optional chaining: an account missing from the mapping used to crash the form.
          return {
            label: getAccountLabel(a, account),
            value: a + '_' + o?.data?.id,
            icon: getIcon(account?.cloud_type!),
            data: {
              accountId: a,
              environmentId: o?.data?.id,
            },
          };
        }),
      }));
      setAccountOptions(options);
    } else {
      setAccountOptions([]);
      setSelectedAccounts([]);
    }
  }, [selectedEnvs]);

  /*set selected accounts
  toCloudAccountId is causing issue, do not include in dependency list*/
  useEffect(() => {
    const accounts = map(formData?.ids, o =>
      map(o?.account_ids, a => {
        const account = accountMapping?.[a];
        // Shared label helper avoids "undefined (undefined)" for unknown accounts.
        return {
          label: getAccountLabel(a, account),
          value: a + '_' + o?.environment_id,
          data: {
            accountId: a,
            environmentId: o?.environment_id,
          },
        };
      }),
    );
    setSelectedAccounts(flatten(accounts));
  }, [formData?.ids, accountMapping]);

  return (
    <Box pt={4}>
      <Form
        isLoading={allowList.isLoading}
        schema={{
          name: {
            type: 'text',
            label: 'Name',
            placeholder: 'Name',
            isRequired: true,
            value: formData?.name,
            onChange: value => updateFormData('name', value),
          },
          environment_ids: {
            type: 'react-select',
            label: 'Environment',
            isRequired: true,
            isDisabled: actionType === 'Update',
            isMulti: true,
            placeholder: 'Select environment...',
            options: environmentOptions,
            onChange: setSelectedEnvs,
            value: selectedEnvs,
            showTotalSelected: true,
            blurInputOnSelect: false,
            showSelectedAtBottom: true,
          },
          account_ids: {
            type: 'react-select',
            label: 'Cloud Account',
            isRequired: true,
            isDisabled: actionType === 'Update',
            placeholder: 'Select account...',
            options: accountOptions,
            isMulti: true,
            value: selectedAccounts,
            onChange: selected => {
              updateFormData('account_ids', selected);
            },
            onClickGroupHeading: options => {
              updateFormData(
                'account_ids',
                unionBy(selectedAccounts, options, 'value'),
              );
            },
            showTotalSelected: true,
            blurInputOnSelect: false,
            showSelectedAtBottom: true,
          },
          criteria: {
            type: 'custom-with-form-control',
            label: 'Criteria',
            component: <AllowListCriteria setIsDisabled={setIsDisabled} />,
          },
          status: {
            type: 'custom-with-form-control',
            label: 'Status',
            component: <AllowListStatus />,
          },
        }}
        styles={{ form: { container: { pl: 14 }, formWidth: '980px' } }}
        buttonOptions={{
          submit: {
            name: 'OK',
            isDisabled:
              !formData?.name ||
              !formData?.ids?.length ||
              allowListAction.isLoading ||
              tagKeys.isLoading ||
              regions.isLoading ||
              isDisabled ||
              allowListStatus.isLoading,
            isLoading: allowListAction.isLoading,
          },
          reset: {
            name: 'CANCEL',
            isVisible: true,
            onClick: onCancel,
          },
        }}
        handleSubmit={handleSubmit}
      />
    </Box>
  );
};
